'use client';

import API from '@/API';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, UseFormReturn } from 'react-hook-form';
import * as z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Icons } from '@/components/ui/icons';
import { Input } from '@/components/ui/input';
import { InputOTP } from '@/components/ui/input-otp';
import { cn, generateExpiryToken } from '@/lib/utils';
import { login, logout } from '@/redux/action';
import { IUserSession } from '@/redux/reducer/session';
import { SlotProps } from 'input-otp';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { useModal } from '../modals/useModal';

const creadentailFormSchema = z.object({
  email: z.string().min(1, 'Email is required'),
  password: z.string().min(1, 'Password is required'),
});

const otpFormSchema = z.object({
  otp: z.string().min(6, {
    message: 'Your one-time password must be 6 characters.',
  }),
  email: z.string().min(1, 'Email is required'),
});

const forgotPasswordEmailSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
});

const forgotPasswordResetSchema = z
  .object({
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirm_password: z.string().min(1, 'Confirm Password is required'),
  })
  .refine((data) => data?.password === data?.confirm_password, {
    message: 'Passwords are not matching',
    path: ['confirm_password'],
  });

type LoginStep =
  | 'credentials'
  | 'login-otp'
  | 'forgot-email'
  | 'forgot-otp'
  | 'forgot-reset';

const LoginForm = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [credentailLoading, setCredentailLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordOtp, setForgotPasswordOtp] = useState('');
  const [step, setStep] = useState<LoginStep>('credentials');
  const { closeModal } = useModal('login');

  useEffect(() => {
    // check if user is already logged in
    const userData = localStorage.getItem('userSession')
      ? JSON.parse(localStorage.getItem('userSession') as any)
      : null;

    if (userData && userData?.token_expiry - new Date().getTime() >= 1) {
      closeModal();
      router.push('/dashboard');
    } else dispatch(logout());
  }, []);

  const creadentialForm = useForm({
    resolver: zodResolver(creadentailFormSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const otpForm = useForm({
    resolver: zodResolver(otpFormSchema),
    defaultValues: {
      otp: '',
      email: '',
    },
  });

  const forgotPasswordEmailForm = useForm({
    resolver: zodResolver(forgotPasswordEmailSchema),
    defaultValues: {
      email: '',
    },
  });

  const forgotPasswordOtpForm = useForm({
    resolver: zodResolver(otpFormSchema),
    defaultValues: {
      otp: '',
      email: '',
    },
  });

  const forgotPasswordResetForm = useForm({
    resolver: zodResolver(forgotPasswordResetSchema),
    defaultValues: {
      password: '',
      confirm_password: '',
    },
  });

  const onCredentailSubmit = (data: any) => {
    setCredentailLoading(true);
    setOtpLoading(true);

    API.auth
      .sendOtp(data)
      .then((response) => {
        if (response) {
          if (response?.to_change_password) {
            return router.push('/reset-password');
          }
          otpForm.setValue('email', data.email);
          otpForm.setValue('otp', '');
          setStep('login-otp');
        }
      })
      .finally(() => {
        setCredentailLoading(false);
        setOtpLoading(false);
      });
  };

  const onSubmit = (data: any) => {
    setOtpLoading(true);
    API.auth
      .verifyOtp(data)
      .then((response) => {
        if (response) {
          if (
            response !== 'The OTP has expired. Please request a new one.' &&
            response !== 'Invalid OTP'
          ) {
            response.token_expiry = generateExpiryToken();
            dispatch(login(response));
            if (response?.to_change_password == true) {
              router.push('/reset-password');
            } else router.push('/dashboard');

            setStep('credentials');
            closeModal();
          }
        }
      })
      .finally(() => {
        setOtpLoading(false);
      });
  };

  const onForgotPasswordEmailSubmit = (data: any) => {
    setForgotPasswordLoading(true);

    API.auth
      .sendForgotPasswordOtp(data)
      .then((response) => {
        if (response) {
          forgotPasswordOtpForm.setValue('email', data.email);
          forgotPasswordOtpForm.setValue('otp', '');
          setForgotPasswordOtp('');
          setStep('forgot-otp');
        }
      })
      .finally(() => {
        setForgotPasswordLoading(false);
      });
  };

  const onForgotPasswordOtpSubmit = (data: any) => {
    setForgotPasswordLoading(true);

    API.auth
      .verifyForgotPasswordOtp(data)
      .then((response) => {
        if (response) {
          setForgotPasswordOtp(data.otp);
          forgotPasswordResetForm.reset();
          setStep('forgot-reset');
        }
      })
      .finally(() => {
        setForgotPasswordLoading(false);
      });
  };

  const onForgotPasswordResetSubmit = (data: any) => {
    setForgotPasswordLoading(true);

    API.auth
      .resetForgotPassword({
        email: forgotPasswordOtpForm.getValues('email'),
        otp: forgotPasswordOtp,
        password: data.password,
      })
      .then((response) => {
        if (response) {
          toast.success('Password reset successfully. Please login.');
          creadentialForm.setValue(
            'email',
            forgotPasswordOtpForm.getValues('email')
          );
          creadentialForm.setValue('password', '');
          forgotPasswordEmailForm.reset();
          forgotPasswordOtpForm.reset();
          forgotPasswordResetForm.reset();
          setForgotPasswordOtp('');
          setStep('credentials');
        }
      })
      .finally(() => {
        setForgotPasswordLoading(false);
      });
  };

  const handleBackToLogin = () => {
    setForgotPasswordOtp('');
    setStep('credentials');
  };

  const handleForgotPasswordBack = () => {
    if (step === 'forgot-reset') {
      setStep('forgot-otp');
      return;
    }

    if (step === 'forgot-otp') {
      setStep('forgot-email');
      return;
    }

    handleBackToLogin();
  };

  const title =
    step === 'forgot-email'
      ? 'Forgot Password'
      : step === 'forgot-otp'
        ? 'Verify OTP'
        : step === 'forgot-reset'
          ? 'Set New Password'
          : 'Welcome back!';
  const showForgotPasswordBack =
    step === 'forgot-otp' || step === 'forgot-reset';

  return (
    <div className='flex h-max flex-col md:flex-row'>
      <div className='h-full w-full items-center justify-center p-4'>
        <div className='flex h-full flex-col items-center justify-center'>
          <div className='mb-4 grid w-full max-w-xl grid-cols-[40px_1fr_40px] items-center'>
            {showForgotPasswordBack ? (
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='h-10 w-10'
                onClick={handleForgotPasswordBack}
                aria-label='Go back'
              >
                <ArrowLeft className='h-4 w-4' />
              </Button>
            ) : (
              <span />
            )}
            <h2 className='text-center text-3xl font-semibold md:text-4xl'>
              {title}
            </h2>
            <span />
          </div>
          {step === 'credentials' ? (
            <CredentialForm
              form={creadentialForm}
              onSubmit={onCredentailSubmit}
              loading={credentailLoading}
              onForgotPassword={() => setStep('forgot-email')}
            />
          ) : step === 'login-otp' ? (
            <OtpForm
              form={otpForm}
              onSubmit={onSubmit}
              loading={otpLoading}
              onResend={() => {
                onCredentailSubmit(creadentialForm.getValues());
              }}
            />
          ) : step === 'forgot-email' ? (
            <ForgotPasswordEmailForm
              form={forgotPasswordEmailForm}
              onSubmit={onForgotPasswordEmailSubmit}
              loading={forgotPasswordLoading}
              onBackToSignIn={handleBackToLogin}
            />
          ) : step === 'forgot-otp' ? (
            <OtpForm
              form={forgotPasswordOtpForm}
              onSubmit={onForgotPasswordOtpSubmit}
              loading={forgotPasswordLoading}
              onResend={() => {
                onForgotPasswordEmailSubmit(
                  forgotPasswordEmailForm.getValues()
                );
              }}
            />
          ) : (
            <ForgotPasswordResetForm
              form={forgotPasswordResetForm}
              onSubmit={onForgotPasswordResetSubmit}
              loading={forgotPasswordLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginForm;

const CredentialForm = ({
  form,
  onSubmit,
  loading,
  onForgotPassword,
}: {
  form: UseFormReturn<
    {
      email: string;
      password: string;
    },
    any,
    undefined
  >;
  onSubmit: (data: any) => void;
  loading: boolean;
  onForgotPassword: () => void;
}) => {
  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className='w-full max-w-xl space-y-4'
        >
          <FormField
            control={form.control}
            name='email'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder='Email' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='password'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input placeholder='Password' {...field} type='password' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <p className='text-sm text-muted-foreground'>
            By continuing, you agree to our{' '}
            <Link href='/legal/terms' className='text-blue-500'>
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link href='/legal/privacy' className='text-blue-500'>
              Privacy Policy
            </Link>
            .
          </p>
          <div className='flex w-full items-center justify-center gap-10'>
            <Button type='submit' className='w-full'>
              {loading && (
                <Icons.spinner className='mr-2 h-4 w-4 animate-spin' />
              )}
              Login
              <ArrowRight className='ml-2 h-4 w-4' />
            </Button>
          </div>
          <div className='flex justify-center'>
            <Button
              type='button'
              variant='link'
              className='h-auto p-0 text-blue-600'
              onClick={onForgotPassword}
            >
              Forgot password?
            </Button>
          </div>
        </form>
      </Form>
    </>
  );
};

interface OtpFormProps {
  form: UseFormReturn<{
    otp: string;
    email: string;
  }>;
  onSubmit: (data: any) => void;
  loading: boolean;
  onResend?: () => void;
}

export const OtpForm = ({
  form,
  onSubmit,
  loading,
  onResend,
}: OtpFormProps) => {
  const [timer, setTimer] = useState(60);

  useEffect(() => {
    if (timer === 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  const handleResend = () => {
    setTimer(60);
    onResend?.(); // trigger resend OTP logic
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='w-full max-w-xl space-y-4'
      >
        <FormField
          control={form.control}
          name='otp'
          render={({ field }) => (
            <FormItem className='w-full'>
              <FormLabel>One-Time Password</FormLabel>
              <FormControl className='flex w-full flex-col items-center'>
                <InputOTP
                  maxLength={6}
                  {...field}
                  containerClassName='group flex w-full items-center has-[:disabled]:opacity-30'
                  render={({ slots }) => (
                    <>
                      <div className='flex'>
                        {slots.slice(0, 3).map((slot, idx) => (
                          <Slot key={idx} {...slot} />
                        ))}
                      </div>

                      <FakeDash />

                      <div className='flex'>
                        {slots.slice(3).map((slot, idx) => (
                          <Slot key={idx} {...slot} />
                        ))}
                      </div>
                    </>
                  )}
                />
              </FormControl>
              <FormDescription>
                Please enter the one-time password sent to your email.
              </FormDescription>
              <FormDescription>
                {timer > 0 ? (
                  <>
                    You can resend the OTP in{' '}
                    <span className='font-semibold'>{timer}s</span>.
                  </>
                ) : (
                  <Button
                    type='button'
                    variant='link'
                    className='h-auto p-0 text-blue-600'
                    onClick={handleResend}
                  >
                    Resend OTP
                  </Button>
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className='flex w-full items-center justify-start gap-10'>
          <Button type='submit' className='w-full' disabled={loading}>
            {loading && <Icons.spinner className='mr-2 h-4 w-4 animate-spin' />}
            Submit
            <ArrowRight className='ml-2 h-4 w-4' />
          </Button>
        </div>
      </form>
    </Form>
  );
};

function Slot(props: SlotProps) {
  return (
    <div
      className={cn(
        `relative h-14 w-12 text-[2rem] md:w-14 lg:w-16`,
        'flex items-center justify-center',
        'transition-all duration-300',
        'border-y border-r border-border first:rounded-l-md first:border-l last:rounded-r-md',
        'group-focus-within:border-accent-foreground/20 group-hover:border-accent-foreground/20',
        'outline outline-0 outline-accent-foreground/20',
        { 'outline-4 outline-accent-foreground': props.isActive }
      )}
    >
      {props.char !== null && <div>{props.char}</div>}
      {props.hasFakeCaret && <FakeCaret />}
    </div>
  );
}

function FakeCaret() {
  return (
    <div className='pointer-events-none absolute inset-0 flex animate-caret-blink items-center justify-center'>
      <div className='h-8 w-px bg-white' />
    </div>
  );
}

function FakeDash() {
  return (
    <div className='flex w-10 items-center justify-center'>
      <div className='h-1 w-3 rounded-full bg-border' />
    </div>
  );
}

const ForgotPasswordEmailForm = ({
  form,
  onSubmit,
  loading,
  onBackToSignIn,
}: {
  form: UseFormReturn<
    {
      email: string;
    },
    any,
    undefined
  >;
  onSubmit: (data: any) => void;
  loading: boolean;
  onBackToSignIn: () => void;
}) => {
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='w-full max-w-xl space-y-4'
      >
        <FormField
          control={form.control}
          name='email'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder='Email' {...field} />
              </FormControl>
              <FormDescription>
                Enter your email and we will send an OTP to verify your account.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className='flex w-full items-center justify-center'>
          <Button type='submit' className='w-full' disabled={loading}>
            {loading && <Icons.spinner className='mr-2 h-4 w-4 animate-spin' />}
            Send OTP
            <ArrowRight className='ml-2 h-4 w-4' />
          </Button>
        </div>
        <div className='flex justify-center'>
          <Button
            type='button'
            variant='link'
            className='h-auto p-0 text-blue-600'
            onClick={onBackToSignIn}
          >
            Back to sign in
          </Button>
        </div>
      </form>
    </Form>
  );
};

const ForgotPasswordResetForm = ({
  form,
  onSubmit,
  loading,
}: {
  form: UseFormReturn<
    {
      password: string;
      confirm_password: string;
    },
    any,
    undefined
  >;
  onSubmit: (data: any) => void;
  loading: boolean;
}) => {
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='w-full max-w-xl space-y-4'
      >
        <FormField
          control={form.control}
          name='password'
          render={({ field }) => (
            <FormItem>
              <FormLabel>New Password</FormLabel>
              <FormControl>
                <Input placeholder='New Password' {...field} type='password' />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='confirm_password'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirm New Password</FormLabel>
              <FormControl>
                <Input
                  placeholder='Confirm New Password'
                  {...field}
                  type='password'
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className='flex w-full items-center justify-center'>
          <Button type='submit' className='w-full' disabled={loading}>
            {loading && <Icons.spinner className='mr-2 h-4 w-4 animate-spin' />}
            Reset Password
            <ArrowRight className='ml-2 h-4 w-4' />
          </Button>
        </div>
      </form>
    </Form>
  );
};
